// =============================================================================
// GOUV GROOT - Widget Gouvernance : Groot Guardian
// Migré depuis management/GrootAgent.jsx le 22/02/2026
// =============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { Shield, AlertTriangle, CheckCircle, FileCode, Play, RefreshCw, MessageSquare, Eye, Filter, Download, ChevronRight, ChevronDown, Clock, XCircle, Database, FolderTree, Search, Code, Table, Layers, Trash2, Loader2, BookOpen, Upload, Lock, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { Input } from '../../../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Composant pour afficher une règle
const RuleCard = ({ rule, onScan }) => {
  const severityColors = {
    critical: 'bg-red-500/20 text-red-400 border-red-500/50',
    high: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
    medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
    low: 'bg-blue-500/20 text-blue-400 border-blue-500/50'
  };

  return (
    <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-slate-600 transition-all">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Badge className={severityColors[rule.severity]}>
              {rule.severity.toUpperCase()}
            </Badge>
            <code className="text-xs text-slate-400 bg-slate-900 px-2 py-0.5 rounded">
              {rule.code}
            </code>
          </div>
          <h4 className="text-white font-medium mb-1">{rule.description}</h4>
          <p className="text-sm text-slate-400">{rule.context}</p>
          {rule.file_patterns && (
            <div className="mt-2 flex flex-wrap gap-1">
              {rule.file_patterns.map((pattern, i) => (
                <span key={i} className="text-xs bg-slate-700 text-slate-300 px-2 py-0.5 rounded">
                  {pattern}
                </span>
              ))}
            </div>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onScan([rule.code])}
          className="text-slate-400 hover:text-white"
        >
          <Play className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

// Composant FileViewer - Aperçu de fichier avec highlighting
const FileViewer = ({ filePath, lineNumber, onClose }) => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadFile = async () => {
      setLoading(true);
      setError(null);
      try {
        const url = `${API_URL}/api/groot/files/view?path=${encodeURIComponent(filePath)}${lineNumber ? `&line=${lineNumber}&context=15` : ''}`;
        const res = await fetch(url);
        if (res.ok) {
          const data = await res.json();
          setContent(data);
        } else {
          setError('Fichier non trouvé');
        }
      } catch (e) {
        setError('Erreur de chargement');
      } finally {
        setLoading(false);
      }
    };
    loadFile();
  }, [filePath, lineNumber]);

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-slate-900 rounded-xl border border-slate-700 w-full max-w-5xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <FileCode className="w-5 h-5 text-emerald-400" />
            <div>
              <h3 className="text-white font-medium">{filePath}</h3>
              {lineNumber && <p className="text-sm text-slate-400">Ligne {lineNumber}</p>}
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <XCircle className="w-5 h-5" />
          </Button>
        </div>
        <ScrollArea className="flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : error ? (
            <div className="text-center py-12 text-red-400">{error}</div>
          ) : content && (
            <div className="p-4 font-mono text-sm">
              {content.lines.map((line) => (
                <div
                  key={line.number}
                  className={`flex ${line.highlighted ? 'bg-red-500/20 border-l-2 border-red-500' : ''}`}
                >
                  <span className="w-12 text-right pr-4 text-slate-500 select-none">{line.number}</span>
                  <pre className={`flex-1 ${line.highlighted ? 'text-red-300' : 'text-slate-300'}`}>
                    {line.content || ' '}
                  </pre>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
        {content && (
          <div className="p-3 border-t border-slate-700 text-xs text-slate-500">
            {content.total_lines} lignes • {content.file_type}
          </div>
        )}
      </div>
    </div>
  );
};

// Composant FileTree - Arborescence des fichiers
const FileTree = ({ onSelectFile }) => {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPath, setCurrentPath] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    loadTree(currentPath);
  }, [currentPath]);

  const loadTree = async (path) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/groot/files/tree?path=${encodeURIComponent(path)}&depth=2`);
      if (res.ok) {
        const data = await res.json();
        setTree(data.tree || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await fetch(`${API_URL}/api/groot/files/search?query=${encodeURIComponent(searchQuery)}&path=backend`);
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSearching(false);
    }
  };

  const renderItem = (item, depth = 0) => (
    <div key={item.path} style={{ paddingLeft: `${depth * 16}px` }}>
      <div
        className={`flex items-center gap-2 py-1 px-2 rounded cursor-pointer hover:bg-slate-700/50 ${item.type === 'file' ? 'text-slate-300' : 'text-slate-400'}`}
        onClick={() => item.type === 'directory' ? setCurrentPath(item.path) : onSelectFile(item.path)}
      >
        {item.type === 'directory' ? (
          <FolderTree className="w-4 h-4 text-yellow-400" />
        ) : (
          <FileCode className="w-4 h-4 text-emerald-400" />
        )}
        <span className="text-sm">{item.name}</span>
        {item.type === 'file' && (
          <span className="text-xs text-slate-500 ml-auto">{(item.size / 1024).toFixed(1)}kb</span>
        )}
      </div>
      {item.children && item.children.map(child => renderItem(child, depth + 1))}
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="flex gap-2">
        <Input
          placeholder="Rechercher dans le code..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          className="bg-slate-800 border-slate-700"
        />
        <Button onClick={handleSearch} disabled={searching} className="bg-slate-700">
          <Search className={`w-4 h-4 ${searching ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Search Results */}
      {searchResults && (
        <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-400">{searchResults.total_results} résultats</span>
            <Button variant="ghost" size="sm" onClick={() => setSearchResults(null)}>
              <XCircle className="w-4 h-4" />
            </Button>
          </div>
          <ScrollArea className="h-[200px]">
            {searchResults.results.map((r, i) => (
              <div
                key={i}
                className="p-2 hover:bg-slate-700/50 rounded cursor-pointer"
                onClick={() => onSelectFile(r.file, r.line)}
              >
                <div className="text-sm text-emerald-400">{r.file}:{r.line}</div>
                <div className="text-xs text-slate-400 truncate">{r.content}</div>
              </div>
            ))}
          </ScrollArea>
        </div>
      )}

      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm">
        <button className="text-emerald-400 hover:underline" onClick={() => setCurrentPath('')}>
          /app
        </button>
        {currentPath && (
          <>
            <ChevronRight className="w-4 h-4 text-slate-500" />
            <span className="text-slate-300">{currentPath}</span>
          </>
        )}
      </div>

      {/* Tree */}
      <ScrollArea className="h-[350px]">
        {loading ? (
          <div className="flex justify-center py-4">
            <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="space-y-1">
            {currentPath && (
              <div
                className="flex items-center gap-2 py-1 px-2 rounded cursor-pointer hover:bg-slate-700/50 text-slate-400"
                onClick={() => setCurrentPath(currentPath.split('/').slice(0, -1).join('/'))}
              >
                <ChevronDown className="w-4 h-4 rotate-90" />
                <span className="text-sm">..</span>
              </div>
            )}
            {tree.map(item => renderItem(item))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
};

// Composant DBInspector - Inspection de la base de données
const DBInspector = () => {
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [collectionData, setCollectionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [indexCheck, setIndexCheck] = useState(null);

  useEffect(() => {
    loadCollections();
  }, []);

  const loadCollections = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/groot/db/collections`);
      if (res.ok) {
        const data = await res.json();
        setCollections(data.collections || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const inspectCollection = async (name) => {
    setSelectedCollection(name);
    try {
      const res = await fetch(`${API_URL}/api/groot/db/collection/${name}?limit=5`);
      if (res.ok) {
        const data = await res.json();
        setCollectionData(data);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const checkIndexes = async () => {
    try {
      const res = await fetch(`${API_URL}/api/groot/db/check-indexes`);
      if (res.ok) {
        const data = await res.json();
        setIndexCheck(data);
        if (data.issues_found > 0) {
          toast.warning(`${data.issues_found} index manquants détectés`);
        } else {
          toast.success('Tous les index sont présents');
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {/* Collections List */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-white font-medium">Collections ({collections.length})</h4>
          <Button size="sm" onClick={checkIndexes} className="bg-slate-700">
            <Layers className="w-4 h-4 mr-1" /> Check Index
          </Button>
        </div>
        
        <ScrollArea className="h-[400px]">
          {loading ? (
            <div className="flex justify-center py-4">
              <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="space-y-2">
              {collections.map((coll) => (
                <div
                  key={coll.name}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                    selectedCollection === coll.name
                      ? 'bg-emerald-500/20 border-emerald-500/50'
                      : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                  }`}
                  onClick={() => inspectCollection(coll.name)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Table className="w-4 h-4 text-emerald-400" />
                      <span className="text-white font-medium">{coll.name}</span>
                    </div>
                    <Badge className="bg-slate-700">{coll.count} docs</Badge>
                  </div>
                  {coll.fields && coll.fields.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {coll.fields.slice(0, 5).map((f, i) => (
                        <span key={i} className="text-xs bg-slate-700 text-slate-300 px-1.5 py-0.5 rounded">
                          {f}
                        </span>
                      ))}
                      {coll.fields.length > 5 && (
                        <span className="text-xs text-slate-500">+{coll.fields.length - 5}</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Index Issues */}
        {indexCheck && indexCheck.issues_found > 0 && (
          <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
            <h5 className="text-orange-400 font-medium mb-2">Index manquants ({indexCheck.issues_found})</h5>
            {indexCheck.issues.map((issue, i) => (
              <div key={i} className="text-sm text-slate-300">
                {issue.collection}.{issue.field}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Collection Detail */}
      <div>
        {collectionData ? (
          <div className="space-y-3">
            <h4 className="text-white font-medium">{collectionData.name}</h4>
            
            {/* Structure */}
            <div className="p-3 bg-slate-800/50 rounded-lg">
              <h5 className="text-slate-400 text-sm mb-2">Structure</h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {Object.entries(collectionData.structure || {}).map(([key, type]) => (
                  <div key={key} className="flex items-center gap-2 text-sm">
                    <span className="text-emerald-400">{key}</span>
                    <span className="text-slate-500">: {type}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Indexes */}
            <div className="p-3 bg-slate-800/50 rounded-lg">
              <h5 className="text-slate-400 text-sm mb-2">Index ({collectionData.indexes?.length || 0})</h5>
              {collectionData.indexes?.map((idx, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <Code className="w-3 h-3 text-slate-500" />
                  <span className="text-slate-300">{idx.name}</span>
                  {idx.unique && <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">unique</Badge>}
                </div>
              ))}
            </div>

            {/* Sample Docs */}
            <div className="p-3 bg-slate-800/50 rounded-lg">
              <h5 className="text-slate-400 text-sm mb-2">Exemples ({collectionData.sample_documents?.length || 0})</h5>
              <ScrollArea className="h-[200px]">
                {collectionData.sample_documents?.map((doc, i) => (
                  <pre key={i} className="text-xs text-slate-300 mb-2 p-2 bg-slate-900 rounded overflow-x-auto">
                    {JSON.stringify(doc, null, 2)}
                  </pre>
                ))}
              </ScrollArea>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-slate-500">
            <div className="text-center">
              <Database className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Sélectionnez une collection</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Composant principal Groot
const GouvGroot = () => {
  const [stats, setStats] = useState(null);
  const [rules, setRules] = useState([]);
  const [scanReport, setScanReport] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [ticketFilter, setTicketFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [memoryStatus, setMemoryStatus] = useState(null);
  const [memoryDocs, setMemoryDocs] = useState([]);
  const [selectedMemoryDoc, setSelectedMemoryDoc] = useState(null);
  const [memoryDocContent, setMemoryDocContent] = useState(null);
  const [memorySearch, setMemorySearch] = useState('');
  const [memorySearchResults, setMemorySearchResults] = useState([]);
  const [uploadingToZoho, setUploadingToZoho] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [fileViewer, setFileViewer] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [loadingChat, setLoadingChat] = useState(true);
  
  // État pour ajout de règle
  const [showAddRule, setShowAddRule] = useState(false);
  const [newRule, setNewRule] = useState({
    code: '',
    severity: 'medium',
    description: '',
    context: '',
    pattern_required: '',
    pattern_forbidden: '',
    file_patterns: ''
  });
  const [addingRule, setAddingRule] = useState(false);
  
  // État pour le test d'isolation multi-tenant
  const [testingIsolation, setTestingIsolation] = useState(false);
  const [isolationTestResult, setIsolationTestResult] = useState(null);

  // Session ID unique pour ce chat (basé sur l'utilisateur ou généré)
  const chatSessionId = 'groot_main';

  // Filtrer les tickets selon la source sélectionnée
  const filteredTickets = tickets.filter(ticket => {
    if (ticketFilter === 'all') return true;
    if (ticketFilter === 'groot_agent') return ticket.source === 'groot_agent';
    if (ticketFilter === 'user') return ticket.source === 'user' || !ticket.source;
    if (ticketFilter === 'cxo') return ticket.source === 'cxo' || ticket.assigned_to === 'cxo';
    return true;
  });

  // Charger les données initiales
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, rulesRes, ticketsRes] = await Promise.all([
        fetch(`${API_URL}/api/groot/stats`),
        fetch(`${API_URL}/api/groot/rules`),
        fetch(`${API_URL}/api/groot/tickets?source=all&status=open`)  // Charger uniquement les tickets ouverts
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (rulesRes.ok) {
        const data = await rulesRes.json();
        setRules(data.rules || []);
      }
      if (ticketsRes.ok) {
        const data = await ticketsRes.json();
        setTickets(data.tickets || []);
      }
    } catch (error) {
      console.error('Erreur chargement Groot:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Charger l'historique du chat
  const loadChatHistory = async () => {
    setLoadingChat(true);
    try {
      const res = await fetch(`${API_URL}/api/groot/chat/history/${chatSessionId}`);
      if (res.ok) {
        const data = await res.json();
        if (data.messages && data.messages.length > 0) {
          setChatMessages(data.messages);
        } else {
          // Message d'accueil si pas d'historique
          setChatMessages([
            { 
              rôle: 'assistant', 
              content: 'Bonjour ! Je suis Groot, l\'agent de surveillance de l\'architecture OmniHub. Je peux analyser votre code, inspecter la base de données et détecter les écarts par rapport aux règles définies.\n\n**Commandes utiles:**\n• "scan" - Lancer un scan rapide\n• "scan complet" - Scan + création de tickets\n• "sync mémoire" - Synchroniser avec PRD.md\n• "aide" - Voir toutes les commandes' 
            }
          ]);
        }
      }
    } catch (error) {
      console.error('Erreur chargement historique chat:', error);
      setChatMessages([
        { rôle: 'assistant', content: 'Bonjour ! Je suis Groot. Tapez "aide" pour voir les commandes disponibles.' }
      ]);
    } finally {
      setLoadingChat(false);
    }
  };

  useEffect(() => {
    loadChatHistory();
  }, []);

  // Effacer l'historique du chat
  const clearChatHistory = async () => {
    if (!window.confirm('Voulez-vous vraiment effacer l\'historique du chat ?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/groot/chat/history/${chatSessionId}`, { method: 'DELETE' });
      if (res.ok) {
        setChatMessages([
          { rôle: 'assistant', content: 'Historique effacé. Comment puis-je vous aider ?' }
        ]);
        toast.success('Historique effacé');
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Synchroniser la mémoire avec PRD.md
  const syncMemory = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API_URL}/api/groot/memory/sync`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setMemoryStatus(data.summary);
        toast.success(`✅ Mémoire synchronisée: ${data.summary.features_detected} features, ${data.summary.pending_tasks} tâches`);
        
        // Ajouter un message dans le chat
        setChatMessages(prev => [...prev, {
          rôle: 'assistant',
          content: `🔄 **Synchronisation mémoire terminée**\n\n- **Features détectées**: ${data.summary.features_detected}\n- **Tâches en attente**: ${data.summary.pending_tasks}\n- **Agents actifs**: ${data.summary.agents_found.join(', ')}\n\nLe fichier DELTA.md a été mis à jour.`
        }]);
      } else {
        toast.error('Erreur lors de la synchronisation');
      }
    } catch (error) {
      console.error('Erreur sync mémoire:', error);
      toast.error('Erreur de connexion');
    } finally {
      setSyncing(false);
    }
  };

  // Charger le statut mémoire
  const loadMemoryStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/groot/memory/status`);
      if (res.ok) {
        const data = await res.json();
        setMemoryStatus(data);
      }
    } catch (e) {
      console.error(e);
    }
  };

  // Charger les documents mémoire
  const loadMemoryDocs = async () => {
    try {
      const res = await fetch(`${API_URL}/api/groot/memory/documents`);
      if (res.ok) {
        const data = await res.json();
        const docs = Object.entries(data.files || {}).map(([key, filename]) => ({
          key,
          filename,
          loaded: data.summary?.documents?.[key] ? true : false,
          hash: data.summary?.documents?.[key]?.hash,
          sections: data.summary?.documents?.[key]?.sections || []
        }));
        setMemoryDocs(docs);
      }
    } catch (e) {
      console.error('Erreur chargement docs mémoire:', e);
    }
  };

  // Charger le contenu d'un document mémoire
  const loadMemoryDocContent = async (key) => {
    try {
      const res = await fetch(`${API_URL}/api/groot/memory/document/${key}`);
      if (res.ok) {
        const data = await res.json();
        setMemoryDocContent(data);
        setSelectedMemoryDoc(key);
      } else {
        toast.error('Document non trouvé');
      }
    } catch (e) {
      toast.error('Erreur chargement document');
    }
  };

  // Rechercher dans la mémoire
  const searchInMemory = async () => {
    if (!memorySearch.trim()) return;
    try {
      const res = await fetch(`${API_URL}/api/groot/memory/search?q=${encodeURIComponent(memorySearch)}`);
      if (res.ok) {
        const data = await res.json();
        setMemorySearchResults(data.results || []);
        toast.success(`${data.count} résultats trouvés`);
      }
    } catch (e) {
      toast.error('Erreur recherche');
    }
  };

  // Télécharger un fichier .md
  const downloadMemoryDoc = async (key, filename) => {
    try {
      const res = await fetch(`${API_URL}/api/groot/memory/document/${key}`);
      if (res.ok) {
        const data = await res.json();
        const content = data.content || '';
        
        // Créer un blob et télécharger
        const blob = new Blob([content], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || `${key}.md`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        toast.success(`✅ ${filename} téléchargé`);
      } else {
        toast.error('Document non trouvé');
      }
    } catch (e) {
      toast.error('Erreur téléchargement');
    }
  };

  // Télécharger tous les fichiers .md en ZIP
  const downloadAllMemoryDocs = async () => {
    try {
      toast.info('Préparation du téléchargement...');
      
      // Charger tous les documents
      const contents = [];
      for (const doc of memoryDocs) {
        try {
          const res = await fetch(`${API_URL}/api/groot/memory/document/${doc.key}`);
          if (res.ok) {
            const data = await res.json();
            contents.push({
              filename: doc.filename,
              content: data.content || ''
            });
          }
        } catch (e) {
          console.warn(`Impossible de charger ${doc.key}`);
        }
      }
      
      // Si un seul fichier, télécharger directement
      if (contents.length === 1) {
        const blob = new Blob([contents[0].content], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = contents[0].filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        // Créer un fichier combiné (pas de ZIP disponible côté client simple)
        // On crée un fichier markdown combiné avec séparateurs
        let combined = `# Mémoire Projet OmniHub\n\nExporté le ${new Date().toLocaleDateString('fr-FR')}\n\n---\n\n`;
        for (const doc of contents) {
          combined += `\n\n# ═══ ${doc.filename} ═══\n\n`;
          combined += doc.content;
          combined += `\n\n---\n`;
        }
        
        const blob = new Blob([combined], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `memoire_projet_${new Date().toISOString().split('T')[0]}.md`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
      
      toast.success(`✅ ${contents.length} fichier(s) téléchargé(s)`);
    } catch (e) {
      toast.error('Erreur téléchargement');
    }
  };

  // Upload vers Zoho Workdrive
  const uploadToZoho = async () => {
    setUploadingToZoho(true);
    try {
      const res = await fetch(`${API_URL}/api/groot/zoho/upload`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        toast.success(`✅ Fichiers uploadés sur Zoho: ${data.files_uploaded?.length || 0} fichier(s)`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur upload Zoho');
      }
    } catch (e) {
      toast.error('Erreur connexion Zoho');
    } finally {
      setUploadingToZoho(false);
    }
  };

  useEffect(() => {
    loadMemoryStatus();
    loadMemoryDocs();
  }, []);

  // Lancer un scan
  const runScan = async (ruleCodes = null, createTickets = false) => {
    setScanning(true);
    try {
      let url = `${API_URL}/api/groot/scan`;
      if (createTickets) {
        url = `${API_URL}/api/groot/scan/full`;
      } else if (ruleCodes) {
        url += `?rules=${ruleCodes.join(',')}`;
      }

      const res = await fetch(url, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setScanReport(data.report);
        toast.success(`Scan terminé: ${data.report.summary.total} violations détectées`);
        loadData();
      } else {
        toast.error('Erreur lors du scan');
      }
    } catch (error) {
      console.error('Erreur scan:', error);
      toast.error('Erreur lors du scan');
    } finally {
      setScanning(false);
    }
  };

  // Ouvrir un fichier
  const openFile = (path, line = null) => {
    setFileViewer({ path, line });
  };

  // Gérer le chat avec LLM
  const handleChatSubmit = async (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;

    const userMessage = chatInput.trim();
    setChatMessages(prev => [...prev, { rôle: 'user', content: userMessage }]);
    setChatInput('');

    try {
      // Appeler l'API de chat LLM
      const res = await fetch(`${API_URL}/api/groot/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMessage, session_id: chatSessionId })
      });

      if (res.ok) {
        const data = await res.json();
        
        // Afficher la réponse
        setChatMessages(prev => [...prev, { rôle: 'assistant', content: data.response }]);
        
        // Exécuter l'action si détectée
        if (data.action === 'scan') {
          runScan();
        } else if (data.action === 'scan_full') {
          runScan(null, true);
        } else if (data.action === 'list_rules') {
          setActiveTab('rules');
        } else if (data.action === 'show_violations') {
          setActiveTab('violations');
        } else if (data.action === 'show_tickets') {
          setActiveTab('tickets');
        } else if (data.action === 'view_files') {
          setActiveTab('files');
        } else if (data.action === 'view_db') {
          setActiveTab('database');
        } else if (data.action === 'sync_memory') {
          syncMemory();
        }
      } else {
        setChatMessages(prev => [...prev, { 
          rôle: 'assistant', 
          content: 'Désolé, une erreur est survenue. Réessayez ou tapez "aide".' 
        }]);
      }
    } catch (error) {
      console.error('Erreur chat:', error);
      setChatMessages(prev => [...prev, { 
        rôle: 'assistant', 
        content: 'Erreur de connexion. Vérifiez que le backend est actif.' 
      }]);
    }
  };

  // Ajouter une règle
  const handleAddRule = async () => {
    if (!newRule.code || !newRule.description) {
      toast.error("Le code et la description sont obligatoires");
      return;
    }
    
    setAddingRule(true);
    try {
      const res = await fetch(`${API_URL}/api/groot/rules/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...newRule,
          file_patterns: newRule.file_patterns ? newRule.file_patterns.split(',').map(p => p.trim()) : []
        })
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        toast.success(`Règle ${newRule.code} créée`);
        setShowAddRule(false);
        setNewRule({
          code: '',
          severity: 'medium',
          description: '',
          context: '',
          pattern_required: '',
          pattern_forbidden: '',
          file_patterns: ''
        });
        // Recharger les règles
        loadData();
      } else {
        toast.error(data.detail || "Erreur lors de la création");
      }
    } catch (err) {
      toast.error("Erreur de connexion");
    } finally {
      setAddingRule(false);
    }
  };

  // Test d'isolation Multi-Tenant
  const runIsolationTest = async () => {
    setTestingIsolation(true);
    setIsolationTestResult(null);
    try {
      const res = await fetch(`${API_URL}/api/groot/test/multi-tenant`);
      if (res.ok) {
        const data = await res.json();
        setIsolationTestResult(data);
        
        if (data.success) {
          toast.success(`✅ Test d'isolation réussi! ${data.results?.summary?.passed || 0} vérifications passées`);
        } else {
          toast.error(`❌ Test d'isolation échoué: ${data.results?.summary?.failed || 0} problèmes détectés`);
        }
        
        // Ajouter un message dans le chat
        const summary = data.results?.summary || {};
        setChatMessages(prev => [...prev, {
          rôle: 'assistant',
          content: `🔒 **Test d'Isolation Multi-Tenant**\n\n${data.success ? '✅ **RÉUSSI**' : '❌ **ÉCHOUÉ**'}\n\n- **Vérifications passées**: ${summary.passed || 0}\n- **Avertissements**: ${summary.warnings || 0}\n- **Échecs**: ${summary.failed || 0}\n\n${!data.success ? '⚠️ Consultez les résultats détaillés ci-dessous.' : 'Toutes les données sont correctement isolées entre tenants.'}`
        }]);
      } else {
        const error = await res.json();
        toast.error(error.detail || "Erreur lors du test");
        setIsolationTestResult({ success: false, error: error.detail });
      }
    } catch (err) {
      toast.error("Erreur de connexion au test d'isolation");
      setIsolationTestResult({ success: false, error: err.message });
    } finally {
      setTestingIsolation(false);
    }
  };

  // Filtrer les règles
  const filteredRules = rules.filter(rule => {
    if (filterSeverity !== 'all' && rule.severity !== filterSeverity) return false;
    if (searchTerm && !rule.description.toLowerCase().includes(searchTerm.toLowerCase()) && !rule.code.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-slate-950 p-6" data-testid="groot-agent-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
            <Shield className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Groot Agent</h1>
            <p className="text-slate-400">Surveillance de l'architecture OmniHub</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={loadData}
            disabled={loading}
            className="border-slate-700 text-slate-300"
            data-testid="groot-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
          <Button
            variant="outline"
            onClick={syncMemory}
            disabled={syncing}
            className="border-emerald-700 text-emerald-400 hover:bg-emerald-900/20"
            data-testid="groot-sync-memory-btn"
          >
            <Layers className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Sync...' : 'Sync Mémoire'}
          </Button>
          <Button
            onClick={() => runScan(null, false)}
            disabled={scanning}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="groot-scan-btn"
          >
            <Play className={`w-4 h-4 mr-2 ${scanning ? 'animate-pulse' : ''}`} />
            {scanning ? 'Scan en cours...' : 'Lancer un scan'}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{rules.length}</p>
                <p className="text-sm text-slate-400">Règles</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats?.total_open || 0}</p>
                <p className="text-sm text-slate-400">Tickets</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                <FileCode className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{scanReport?.stats?.files_scanned || '-'}</p>
                <p className="text-sm text-slate-400">Fichiers</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{scanReport?.stats?.violations_found || '-'}</p>
                <p className="text-sm text-slate-400">Violations</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Database className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats?.db_connected ? 'OK' : '-'}</p>
                <p className="text-sm text-slate-400">MongoDB</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
        {/* Left Panel - Tabs */}
        <div className="col-span-2">
          <Card className="bg-slate-900 border-slate-800">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <CardHeader className="border-b border-slate-800 pb-0">
                <TabsList className="bg-slate-800/50">
                  <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
                  <TabsTrigger value="rules">Règles ({rules.length})</TabsTrigger>
                  <TabsTrigger value="violations">Violations {scanReport && `(${scanReport.summary.total})`}</TabsTrigger>
                  <TabsTrigger value="tickets">Tickets ({filteredTickets.length})</TabsTrigger>
                  <TabsTrigger value="memory">Mémoire</TabsTrigger>
                  <TabsTrigger value="files">Fichiers</TabsTrigger>
                  <TabsTrigger value="database">Base de données</TabsTrigger>
                </TabsList>
              </CardHeader>
              <CardContent className="p-4">
                {/* Dashboard Tab */}
                <TabsContent value="dashboard" className="mt-0">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-800/30 rounded-lg">
                      <h4 className="text-white font-medium mb-3">Règles par sévérité</h4>
                      <div className="space-y-2">
                        {['critical', 'high', 'medium', 'low'].map(sev => (
                          <div key={sev} className="flex items-center justify-between">
                            <span className={`text-${sev === 'critical' ? 'red' : sev === 'high' ? 'orange' : sev === 'medium' ? 'yellow' : 'blue'}-400 capitalize`}>{sev}</span>
                            <Badge className={`bg-${sev === 'critical' ? 'red' : sev === 'high' ? 'orange' : sev === 'medium' ? 'yellow' : 'blue'}-500/20`}>
                              {rules.filter(r => r.severity === sev).length}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="p-4 bg-slate-800/30 rounded-lg">
                      <h4 className="text-white font-medium mb-3">Dernier scan</h4>
                      {scanReport ? (
                        <div className="space-y-2 text-sm">
                          <p className="text-slate-400">Fichiers: <span className="text-white">{scanReport.stats.files_scanned}</span></p>
                          <p className="text-slate-400">Violations: <span className="text-white">{scanReport.summary.total}</span></p>
                          <p className="text-slate-400">Critical: <span className="text-red-400">{scanReport.summary.critical}</span></p>
                        </div>
                      ) : <p className="text-slate-400 text-sm">Aucun scan effectué</p>}
                    </div>
                    <div className="col-span-2 p-4 bg-slate-800/30 rounded-lg">
                      <h4 className="text-white font-medium mb-3">Actions rapides</h4>
                      <div className="flex gap-3">
                        <Button onClick={() => runScan(null, false)} disabled={scanning} className="flex-1 bg-slate-700 hover:bg-slate-600">
                          <Play className="w-4 h-4 mr-2" /> Scan rapide
                        </Button>
                        <Button onClick={() => runScan(null, true)} disabled={scanning} className="flex-1 bg-orange-600 hover:bg-orange-700">
                          <AlertTriangle className="w-4 h-4 mr-2" /> Scan + Tickets
                        </Button>
                        <Button 
                          onClick={runIsolationTest} 
                          disabled={testingIsolation} 
                          className="flex-1 bg-purple-600 hover:bg-purple-700"
                          data-testid="groot-isolation-test-btn"
                        >
                          <Lock className={`w-4 h-4 mr-2 ${testingIsolation ? 'animate-spin' : ''}`} />
                          {testingIsolation ? 'Test en cours...' : 'Test Multi-Tenant'}
                        </Button>
                      </div>
                    </div>
                    
                    {/* Résultat du test d'isolation */}
                    {isolationTestResult && (
                      <div className={`col-span-2 p-4 rounded-lg border ${
                        isolationTestResult.success 
                          ? 'bg-emerald-500/10 border-emerald-500/30' 
                          : 'bg-red-500/10 border-red-500/30'
                      }`}>
                        <div className="flex items-center justify-between mb-3">
                          <h4 className={`font-medium flex items-center gap-2 ${
                            isolationTestResult.success ? 'text-emerald-400' : 'text-red-400'
                          }`}>
                            {isolationTestResult.success ? (
                              <><CheckCircle className="w-5 h-5" /> Test d'Isolation Réussi</>
                            ) : (
                              <><XCircle className="w-5 h-5" /> Test d'Isolation Échoué</>
                            )}
                          </h4>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => setIsolationTestResult(null)}
                            className="text-slate-400 hover:text-white"
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        {isolationTestResult.results?.summary && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                            <div className="p-2 bg-slate-900/50 rounded text-center">
                              <p className="text-lg font-bold text-emerald-400">{isolationTestResult.results.summary.passed || 0}</p>
                              <p className="text-xs text-slate-400">Passés</p>
                            </div>
                            <div className="p-2 bg-slate-900/50 rounded text-center">
                              <p className="text-lg font-bold text-yellow-400">{isolationTestResult.results.summary.warnings || 0}</p>
                              <p className="text-xs text-slate-400">Avertissements</p>
                            </div>
                            <div className="p-2 bg-slate-900/50 rounded text-center">
                              <p className="text-lg font-bold text-red-400">{isolationTestResult.results.summary.failed || 0}</p>
                              <p className="text-xs text-slate-400">Échecs</p>
                            </div>
                            <div className="p-2 bg-slate-900/50 rounded text-center">
                              <p className="text-lg font-bold text-blue-400">{(isolationTestResult.results.passed?.length || 0) + (isolationTestResult.results.warnings?.length || 0) + (isolationTestResult.results.failed?.length || 0)}</p>
                              <p className="text-xs text-slate-400">Tests</p>
                            </div>
                          </div>
                        )}
                        
                        {/* Liste des résultats détaillés */}
                        <ScrollArea className="h-[200px]">
                          <div className="space-y-2">
                            {/* Échecs en premier */}
                            {isolationTestResult.results?.failed?.map((msg, idx) => (
                              <div 
                                key={`failed-${idx}`}
                                className="p-2 rounded border bg-red-500/10 border-red-500/20"
                              >
                                <div className="flex items-center gap-2">
                                  <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                                  <span className="text-sm text-red-300">{msg}</span>
                                </div>
                              </div>
                            ))}
                            
                            {/* Avertissements */}
                            {isolationTestResult.results?.warnings?.map((msg, idx) => (
                              <div 
                                key={`warning-${idx}`}
                                className="p-2 rounded border bg-yellow-500/10 border-yellow-500/20"
                              >
                                <div className="flex items-center gap-2">
                                  <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                                  <span className="text-sm text-yellow-300">{msg}</span>
                                </div>
                              </div>
                            ))}
                            
                            {/* Tests passés */}
                            {isolationTestResult.results?.passed?.map((msg, idx) => (
                              <div 
                                key={`passed-${idx}`}
                                className="p-2 rounded border bg-emerald-500/10 border-emerald-500/20"
                              >
                                <div className="flex items-center gap-2">
                                  <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                                  <span className="text-sm text-emerald-300">{msg}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                        
                        {isolationTestResult.error && (
                          <p className="text-sm text-red-400">{isolationTestResult.error}</p>
                        )}
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Rules Tab */}
                <TabsContent value="rules" className="mt-0">
                  <div className="space-y-4">
                    <div className="flex gap-3">
                      <Input placeholder="Rechercher une règle..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="flex-1 bg-slate-800 border-slate-700" />
                      <select value={filterSeverity} onChange={(e) => setFilterSeverity(e.target.value)} className="bg-slate-800 border border-slate-700 rounded-md px-3 text-white">
                        <option value="all">Toutes</option>
                        <option value="critical">Critical</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                      </select>
                      <Button onClick={() => setShowAddRule(!showAddRule)} variant={showAddRule ? "secondary" : "default"} className="bg-emerald-600 hover:bg-emerald-700">
                        {showAddRule ? "Annuler" : "+ Règle"}
                      </Button>
                    </div>
                    
                    {/* Formulaire d'ajout de règle */}
                    {showAddRule && (
                      <div className="p-4 bg-slate-800/80 rounded-lg border border-emerald-500/50 space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-slate-400 mb-1 block">Code *</label>
                            <Input placeholder="ex: UI-010" value={newRule.code} onChange={(e) => setNewRule({...newRule, code: e.target.value.toUpperCase()})} className="bg-slate-900 border-slate-700" />
                          </div>
                          <div>
                            <label className="text-xs text-slate-400 mb-1 block">Sévérité</label>
                            <select value={newRule.severity} onChange={(e) => setNewRule({...newRule, severity: e.target.value})} className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-white">
                              <option value="critical">Critical</option>
                              <option value="high">High</option>
                              <option value="medium">Medium</option>
                              <option value="low">Low</option>
                            </select>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-slate-400 mb-1 block">Description *</label>
                          <Input placeholder="Description de la règle" value={newRule.description} onChange={(e) => setNewRule({...newRule, description: e.target.value})} className="bg-slate-900 border-slate-700" />
                        </div>
                        <div>
                          <label className="text-xs text-slate-400 mb-1 block">Contexte</label>
                          <Input placeholder="Pourquoi cette règle est importante" value={newRule.context} onChange={(e) => setNewRule({...newRule, context: e.target.value})} className="bg-slate-900 border-slate-700" />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-slate-400 mb-1 block">Pattern requis (regex)</label>
                            <Input placeholder="ex: @require_permission" value={newRule.pattern_required} onChange={(e) => setNewRule({...newRule, pattern_required: e.target.value})} className="bg-slate-900 border-slate-700 font-mono text-sm" />
                          </div>
                          <div>
                            <label className="text-xs text-slate-400 mb-1 block">Pattern interdit (regex)</label>
                            <Input placeholder="ex: delete_one|drop" value={newRule.pattern_forbidden} onChange={(e) => setNewRule({...newRule, pattern_forbidden: e.target.value})} className="bg-slate-900 border-slate-700 font-mono text-sm" />
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-slate-400 mb-1 block">Fichiers cibles (séparés par virgule)</label>
                          <Input placeholder="ex: backend/**/*.py, frontend/**/*.jsx" value={newRule.file_patterns} onChange={(e) => setNewRule({...newRule, file_patterns: e.target.value})} className="bg-slate-900 border-slate-700 font-mono text-sm" />
                        </div>
                        <div className="flex justify-end">
                          <Button onClick={handleAddRule} disabled={addingRule || !newRule.code || !newRule.description} className="bg-emerald-600 hover:bg-emerald-700">
                            {addingRule ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Création...</> : "Créer la règle"}
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    <ScrollArea className="h-[400px]">
                      <div className="space-y-3">
                        {filteredRules.map((rule) => <RuleCard key={rule.code} rule={rule} onScan={runScan} />)}
                      </div>
                    </ScrollArea>
                  </div>
                </TabsContent>

                {/* Violations Tab */}
                <TabsContent value="violations" className="mt-0">
                  {scanReport ? (
                    <ScrollArea className="h-[400px]">
                      <div className="space-y-4">
                        {Object.entries(scanReport.by_rule || {}).map(([code, data]) => (
                          <div key={code} className="p-4 bg-slate-800/30 rounded-lg">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <code className="text-sm text-emerald-400">{code}</code>
                                <Badge className={data.severity === 'critical' ? 'bg-red-500/20 text-red-400' : 'bg-orange-500/20 text-orange-400'}>
                                  {data.count} violations
                                </Badge>
                              </div>
                            </div>
                            <p className="text-sm text-slate-300 mb-3">{data.description}</p>
                            <div className="space-y-1">
                              {[...new Set(data.files)].slice(0, 5).map((file, i) => (
                                <div key={i} className="text-xs text-slate-400 flex items-center gap-2 cursor-pointer hover:text-emerald-400" onClick={() => openFile(file)}>
                                  <FileCode className="w-3 h-3" /> {file}
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  ) : (
                    <div className="text-center py-12">
                      <Shield className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                      <p className="text-slate-400">Lancez un scan pour voir les violations</p>
                    </div>
                  )}
                </TabsContent>

                {/* Tickets Tab */}
                <TabsContent value="tickets" className="mt-0">
                  <div className="space-y-4">
                    {/* Filtres de tickets */}
                    <div className="flex items-center gap-2 pb-2 border-b border-slate-700">
                      <span className="text-xs text-slate-400">Source:</span>
                      <div className="flex gap-1">
                        {[
                          { key: 'all', label: 'Tous' },
                          { key: 'groot_agent', label: 'System' },
                          { key: 'user', label: 'Utilisateur' },
                          { key: 'cxo', label: 'CXO' }
                        ].map(f => (
                          <Button
                            key={f.key}
                            size="sm"
                            variant={ticketFilter === f.key ? 'default' : 'outline'}
                            onClick={() => setTicketFilter(f.key)}
                            className={`h-7 text-xs ${ticketFilter === f.key ? 'bg-emerald-600' : 'border-slate-700'}`}
                            data-testid={`ticket-filter-${f.key}`}
                          >
                            {f.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                    
                    <ScrollArea className="h-[350px]">
                      {filteredTickets.length > 0 ? (
                        <div className="space-y-3">
                          {filteredTickets.map((ticket, idx) => (
                            <div 
                              key={ticket.ticket_id || ticket._id || idx} 
                              className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/50 cursor-pointer hover:border-slate-600" 
                              onClick={() => ticket.file_path && openFile(ticket.file_path, ticket.line_number)}
                            >
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <Badge className={
                                    ticket.risk_level === 'critical' || ticket.priority === 'critical' ? 'bg-red-500/20 text-red-400' :
                                    ticket.risk_level === 'high' || ticket.priority === 'high' ? 'bg-orange-500/20 text-orange-400' :
                                    'bg-yellow-500/20 text-yellow-400'
                                  }>
                                    {ticket.rule_code || ticket.type || 'TICKET'}
                                  </Badge>
                                  <Badge variant="outline" className="text-xs border-slate-600">
                                    {ticket.source === 'groot_agent' ? 'System' : ticket.source === 'user' ? 'User' : 'CXO'}
                                  </Badge>
                                </div>
                                <span className="text-xs text-slate-500">
                                  {new Date(ticket.created_at).toLocaleDateString('fr-FR')}
                                </span>
                              </div>
                              <p className="text-sm text-slate-300 mb-1 line-clamp-1">{ticket.title || ticket.rule_description}</p>
                              {ticket.file_path && (
                                <p className="text-xs text-slate-500 truncate">
                                  {ticket.module && <span className="text-emerald-400">[{ticket.module}]</span>} {ticket.file_path}
                                  {ticket.line_number && `:${ticket.line_number}`}
                                </p>
                              )}
                              {ticket.risk_level && (
                                <div className="flex items-center gap-2 mt-2">
                                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                                    ticket.risk_level === 'critical' ? 'bg-red-500/20 text-red-400' :
                                    ticket.risk_level === 'high' ? 'bg-orange-500/20 text-orange-400' :
                                    ticket.risk_level === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                                    'bg-blue-500/20 text-blue-400'
                                  }`}>
                                    Risque: {ticket.risk_level}
                                  </span>
                                  {ticket.divergence_type && (
                                    <span className="text-xs text-slate-500">
                                      {ticket.divergence_type}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <CheckCircle className="w-12 h-12 text-emerald-500/50 mx-auto mb-4" />
                          <p className="text-slate-400">Aucun ticket {ticketFilter !== 'all' ? `(filtre: ${ticketFilter})` : ''}</p>
                        </div>
                      )}
                    </ScrollArea>
                  </div>
                </TabsContent>

                {/* Memory Tab */}
                <TabsContent value="memory" className="mt-0">
                  <div className="space-y-4">
                    {/* Header avec actions */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <BookOpen className="w-5 h-5 text-emerald-400" />
                        <span className="text-white font-medium">Documents de Référence</span>
                        <Badge variant="outline" className="border-slate-600">{memoryDocs.length} fichiers</Badge>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={downloadAllMemoryDocs}
                          className="border-slate-700"
                          data-testid="groot-download-all-btn"
                        >
                          <Download className="w-4 h-4 mr-1" /> Tout
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { loadMemoryDocs(); toast.success('Mémoire rechargée'); }}
                          className="border-slate-700"
                        >
                          <RefreshCw className="w-4 h-4 mr-1" /> Recharger
                        </Button>
                        <Button
                          size="sm"
                          onClick={uploadToZoho}
                          disabled={uploadingToZoho}
                          className="bg-blue-600 hover:bg-blue-700"
                          data-testid="groot-upload-zoho-btn"
                        >
                          <Upload className={`w-4 h-4 mr-1 ${uploadingToZoho ? 'animate-spin' : ''}`} />
                          {uploadingToZoho ? 'Upload...' : 'Zoho'}
                        </Button>
                      </div>
                    </div>

                    {/* Recherche */}
                    <div className="flex gap-2">
                      <Input
                        value={memorySearch}
                        onChange={(e) => setMemorySearch(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && searchInMemory()}
                        placeholder="Rechercher dans la mémoire (RBAC, workflow, etc.)"
                        className="bg-slate-800 border-slate-700"
                      />
                      <Button onClick={searchInMemory} className="bg-emerald-600">
                        <Search className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Résultats de recherche */}
                    {memorySearchResults.length > 0 && (
                      <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                        <h5 className="text-sm font-medium text-white mb-2">Résultats ({memorySearchResults.length})</h5>
                        <ScrollArea className="h-[100px]">
                          <div className="space-y-1">
                            {memorySearchResults.map((r, i) => (
                              <div 
                                key={i} 
                                className="text-xs p-2 bg-slate-900/50 rounded cursor-pointer hover:bg-slate-700"
                                onClick={() => loadMemoryDocContent(r.document)}
                              >
                                <span className="text-emerald-400">[{r.document}]</span>
                                <span className="text-slate-400 ml-2">L{r.line}:</span>
                                <span className="text-slate-300 ml-1">{r.content}</span>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </div>
                    )}

                    {/* Liste des documents et contenu */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                      {/* Liste des documents */}
                      <div className="col-span-1">
                        <ScrollArea className="h-[300px]">
                          <div className="space-y-1">
                            {memoryDocs.map(doc => (
                              <div
                                key={doc.key}
                                className={`p-2 rounded text-sm group ${
                                  selectedMemoryDoc === doc.key 
                                    ? 'bg-emerald-600/20 border border-emerald-500/50' 
                                    : 'bg-slate-800/50 hover:bg-slate-700/50'
                                }`}
                              >
                                <div 
                                  className="flex items-center justify-between cursor-pointer"
                                  onClick={() => loadMemoryDocContent(doc.key)}
                                >
                                  <span className="text-slate-300 truncate flex-1">{doc.filename}</span>
                                  <div className="flex items-center gap-1">
                                    {doc.loaded && <CheckCircle className="w-3 h-3 text-emerald-400 flex-shrink-0" />}
                                    <button
                                      onClick={(e) => { e.stopPropagation(); downloadMemoryDoc(doc.key, doc.filename); }}
                                      className="opacity-0 group-hover:opacity-100 p-1 hover:bg-slate-600 rounded transition-opacity"
                                      title="Télécharger"
                                    >
                                      <Download className="w-3 h-3 text-slate-400 hover:text-white" />
                                    </button>
                                  </div>
                                </div>
                                <span className="text-xs text-slate-500">{doc.key}</span>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </div>

                      {/* Contenu du document */}
                      <div className="col-span-2">
                        {memoryDocContent ? (
                          <div className="bg-slate-800/30 rounded-lg p-3 h-[300px] overflow-hidden">
                            <div className="flex items-center justify-between mb-2">
                              <h5 className="text-white font-medium">{memoryDocContent.filename}</h5>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs border-slate-600">
                                  {memoryDocContent.sections?.length || 0} sections
                                </Badge>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => downloadMemoryDoc(selectedMemoryDoc, memoryDocContent.filename)}
                                  className="border-slate-600 h-6 px-2"
                                  data-testid="groot-download-doc-btn"
                                >
                                  <Download className="w-3 h-3 mr-1" /> .md
                                </Button>
                              </div>
                            </div>
                            <ScrollArea className="h-[250px]">
                              <pre className="text-xs text-slate-300 whitespace-pre-wrap font-mono">
                                {memoryDocContent.content?.slice(0, 5000)}
                                {memoryDocContent.content?.length > 5000 && '\n\n... (tronqué)'}
                              </pre>
                            </ScrollArea>
                          </div>
                        ) : (
                          <div className="bg-slate-800/30 rounded-lg p-3 h-[300px] flex items-center justify-center">
                            <p className="text-slate-500 text-sm">Sélectionnez un document pour voir son contenu</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Files Tab */}
                <TabsContent value="files" className="mt-0">
                  <FileTree onSelectFile={(path, line) => openFile(path, line)} />
                </TabsContent>

                {/* Database Tab */}
                <TabsContent value="database" className="mt-0">
                  <DBInspector />
                </TabsContent>
              </CardContent>
            </Tabs>
          </Card>
        </div>

        {/* Right Panel - Chat */}
        <div className="col-span-1">
          <Card className="bg-slate-900 border-slate-800 h-[500px] flex flex-col">
            <CardHeader className="border-b border-slate-800 flex-shrink-0">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-emerald-400" />
                  Chat Groot
                </CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={clearChatHistory}
                  className="text-slate-400 hover:text-red-400"
                  data-testid="groot-clear-chat-btn"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <CardDescription>Posez vos questions (historique sauvegardé)</CardDescription>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col p-4 overflow-hidden">
              {loadingChat ? (
                <div className="flex-1 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto mb-4 pr-2" style={{ maxHeight: '350px' }}>
                  <div className="space-y-4">
                    {chatMessages.map((msg, i) => (
                      <div key={i} className={`p-3 rounded-lg whitespace-pre-wrap text-sm ${msg.rôle === 'assistant' ? 'bg-slate-800/50 text-slate-300' : 'bg-emerald-600/20 text-emerald-300 ml-8'}`}>
                        {msg.content}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <form onSubmit={handleChatSubmit} className="flex gap-2 flex-shrink-0">
                <Input value={chatInput} onChange={(e) => setChatInput(e.target.value)} placeholder="scan, règles, fichiers, db..." className="bg-slate-800 border-slate-700" />
                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* File Viewer Modal */}
      {fileViewer && (
        <FileViewer filePath={fileViewer.path} lineNumber={fileViewer.line} onClose={() => setFileViewer(null)} />
      )}
    </div>
  );
};

export default GouvGroot;
